import { useEffect } from "react";
import { useShallow } from "zustand/react/shallow";
import { RESOURCE_KINDS, upgradeBill, type Resources } from "@empire/shared";
import { predictedResources, useVillageStore } from "../../store/village.js";
import { ResourceIcon } from "./ResourceIcon.js";
import { useEditorStore } from "../../store/editor.js";
import { useIsDirty, useSaveStore } from "../../store/save.js";
import { useSessionStore } from "../../store/session.js";

/**
 * Saving, from the defender's side (S2-6).
 *
 * Three things live here and they are deliberately not one thing: the button
 * that saves, the strip that explains why a save did not happen, and the
 * browser prompt that catches a closing tab. Each covers a different way to
 * lose a maze.
 */

/**
 * The prompt when you close the tab with unsaved work.
 *
 * Browsers deliberately ignore the message text and show their own wording, so
 * there is no point writing one — what matters is that the listener only exists
 * while there is something to lose. Registering it permanently would nag people
 * who have saved, and a prompt that always appears is a prompt nobody reads.
 */
export function useUnsavedGuard(): void {
  const dirty = useIsDirty();

  useEffect(() => {
    if (!dirty) return;
    const onLeave = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);
}

export function SaveButton(): JSX.Element {
  const validation = useEditorStore((s) => s.validation);
  const status = useSaveStore((s) => s.status);
  const savedAt = useSaveStore((s) => s.savedAt);
  const save = useSaveStore((s) => s.save);
  const sessionStatus = useSessionStore((s) => s.status);
  const dirty = useIsDirty();

  const connecting = sessionStatus === "unknown" || sessionStatus === "connecting";
  const noSession = sessionStatus === "lost" || sessionStatus === "offline";
  const busy = status === "saving";
  const illegal = !validation.ok;

  const label = busy
    ? "Saving…"
    : connecting
      ? "…"
      : dirty
        ? "Save"
        : savedAt
          ? "Saved"
          : "Save";

  const why = illegal
    ? "Fix the maze before saving — the server checks the same rules."
    : connecting
      ? "Signing in…"
      : noSession
        ? "Not connected to the server."
        : dirty
          ? "Save this defence to your village"
          : "Nothing has changed since the last save";

  const { short } = useUpgradeBill();
  const disabled = busy || connecting || illegal || noSession || !dirty || short !== null;

  return (
    <button
      type="button"
      data-testid="save-button"
      title={why}
      aria-label={why}
      disabled={disabled}
      onClick={() => void save()}
      className={`h-11 shrink-0 rounded-lg px-4 text-xs font-bold transition-colors ${
        dirty && !disabled
          ? "bg-gold text-bg"
          : "border border-panel-edge bg-panel text-ink-dim"
      } disabled:opacity-45`}
    >
      {label}
      {dirty && !busy ? <span className="ml-1.5 align-middle text-[15px] leading-none">•</span> : null}
    </button>
  );
}

/**
 * Why the last save did not happen, or why there is nobody to save for.
 *
 * A banner only ever appears when something is actually wrong. The "lost"
 * case is the harsh one and it says so: a guest account lives on this device
 * and nowhere else, so there is no recovery to offer — only starting again,
 * and only if the player says so.
 */
export function SaveBanner(): JSX.Element | null {
  const problem = useSaveStore((s) => s.problem);
  const dismiss = useSaveStore((s) => s.dismissProblem);
  const save = useSaveStore((s) => s.save);
  const sessionStatus = useSessionStore((s) => s.status);
  const connect = useSessionStore((s) => s.connect);
  const startOver = useSessionStore((s) => s.startOver);

  if (sessionStatus === "lost") {
    return (
      <Strip tone="danger" testId="session-lost">
        <span>
          This device is signed out, and a guest account cannot be recovered. Your maze is still
          on screen — starting a new village will save it there.
        </span>
        <Action label="Start a new village" onClick={() => void startOver()} />
      </Strip>
    );
  }

  if (sessionStatus === "offline" && !problem) {
    return (
      <Strip tone="warn" testId="session-offline">
        <span>Can't reach the server. You can keep building; saving will wait.</span>
        <Action label="Retry" onClick={() => void connect()} />
      </Strip>
    );
  }

  if (!problem) return null;

  return (
    <Strip tone={problem.kind === "offline" ? "warn" : "danger"} testId="save-problem">
      <span>{problem.message}</span>
      {problem.kind === "offline" ? <Action label="Try again" onClick={() => void save()} /> : null}
      <Action label="Dismiss" onClick={dismiss} subtle />
    </Strip>
  );
}

function Strip({
  tone,
  testId,
  children,
}: {
  tone: "warn" | "danger";
  testId: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      data-testid={testId}
      className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b px-3 py-2 text-[11px] ${
        tone === "danger"
          ? "border-danger/30 bg-danger/10 text-danger"
          : "border-warn/30 bg-warn/10 text-warn"
      }`}
    >
      {children}
    </div>
  );
}

function Action({
  label,
  onClick,
  subtle,
}: {
  label: string;
  onClick: () => void;
  subtle?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`ml-auto h-8 shrink-0 rounded px-2.5 text-[11px] font-semibold ${
        subtle ? "ml-0 opacity-70 hover:opacity-100" : "bg-panel text-ink hover:bg-panel-edge"
      }`}
    >
      {label}
    </button>
  );
}

/**
 * What this save will charge for tower upgrades (D-119), and whether the
 * village can pay it. Upgrades are chosen in the editor and paid on Save.
 */
export function useUpgradeBill(): { bill: Resources | null; short: string | null } {
  const layout = useEditorStore((s) => s.layout);
  const paid = useSaveStore((s) => s.upgradesPaid);
  const held = useVillageStore(useShallow(predictedResources));
  const bill = upgradeBill(layout, paid);
  if (!RESOURCE_KINDS.some((k) => bill[k] > 0)) return { bill: null, short: null };
  const missing = RESOURCE_KINDS.filter((k) => bill[k] > held[k]).map((k) => `${bill[k] - held[k]} ${k}`);
  return { bill, short: missing.length ? `Need ${missing.join(", ")} more.` : null };
}

/** The upgrade bill, beside the Save button; nothing at all when there is none. */
export function UpgradeBill(): JSX.Element | null {
  const { bill, short } = useUpgradeBill();
  if (!bill) return null;
  return (
    <p className="flex flex-wrap items-center gap-x-2 text-[11px] tabular-nums" data-testid="upgrade-bill">
      <span className={short ? "text-danger" : "text-ink-dim"}>{short ?? "Upgrades on save:"}</span>
      {short
        ? null
        : RESOURCE_KINDS.filter((k) => bill[k] > 0).map((k) => (
            <span key={k} className="flex items-center gap-0.5 text-ink">
              <ResourceIcon kind={k} size={10} />
              {bill[k]}
            </span>
          ))}
    </p>
  );
}
